import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../../../context/AuthContext';
import SafetyApprovalModal from './SafetyApprovalModal';

// 목업 데이터 관련 상수 모두 제거됨
function SafetyApprovalTab({ projectInfo, onOpenApprovalModal }) {
  const { user, token } = useContext(AuthContext);
  const userDisplayName = user?.UserName || user?.name || '박학송';
  const userPosition = user?.position || '부장';

  // currentDrafter 등은 hasCustomizedLine state 선언 이후로 이동됨

  // 로컬 스토리지 가상 연동 로직 폐기 (향후 서버 fetch 로 대체)
  const [documents, setDocuments] = useState([]);

  // 상태 판별 도우미 함수들
  const isMyTurn = (doc) => {
    if (doc.doc_status !== 'progress' && doc.docStatus !== 'progress') return false;
    const myId = String(user?.userid || user?.user_id || user?.userId || '사장님아이디없음');
    const myName = userDisplayName;
    return doc.lines?.some(l => l.current_yn === 'y' && (String(l.user_id || l.userId || l.userid) === myId || l.user_name === myName || l.userName === myName));
  };

  // 서버에서 완벽하게 권한 필터링을 해서 보내주므로, 프론트에서는 "어떤 탭(상태)에 띄울 것인지"만 분류하면 됩니다.
  const counts = {
    ALL: documents.length,
    DRAFT: documents.filter(doc => doc.doc_status === 'draft' || doc.doc_status === 'withdraw').length,
    PENDING: documents.filter(doc => isMyTurn(doc)).length,
    // 진행중: 내 결재 차례인 것을 제외한 나머지 진행중 문서 전체
    PROGRESS: documents.filter(doc => doc.doc_status === 'progress').length,
    REJECTED: documents.filter(doc => doc.doc_status === 'reject').length,
    COMPLETED: documents.filter(doc => doc.doc_status === 'complete').length
  };

  const [activeMenu, setActiveMenu] = useState('PENDING'); // ALL, DRAFT, PENDING, PROGRESS, COMPLETED, REJECTED
  const [searchCategory, setSearchCategory] = useState('ALL'); // ALL, TITLE, DRAFTER
  const [searchText, setSearchText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [filePopover, setFilePopover] = useState({ id: null, x: 0, isUp: false, top: 0, bottom: 0 });

  // 팝오버 바깥 영역 클릭 및 스크롤 시 자동 닫기
  useEffect(() => {
    if (!filePopover.id) return;
    const closePopover = () => setFilePopover({ id: null, x: 0, isUp: false, top: 0, bottom: 0 });
    document.addEventListener('click', closePopover);
    window.addEventListener('scroll', closePopover, { capture: true });
    return () => {
      document.removeEventListener('click', closePopover);
      window.removeEventListener('scroll', closePopover, { capture: true });
    };
  }, [filePopover.id]);

  const formatBytes = (bytes) => {
    if (!bytes || isNaN(bytes) || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDownload = async (file) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../../../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      const url = domain + (file.path ? file.path.replace(/\\/g, '/') : '');
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || file.name || 'download';
      a.target = '_blank';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 100);
    } catch(e) {
      console.error(e);
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleView = async (file) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../../../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      const url = domain + (file.path ? file.path.replace(/\\/g, '/') : '');
      window.open(url, '_blank');
    } catch(e) {
      console.error(e);
      alert('파일 열기 중 오류가 발생했습니다.');
    }
  };

  const handleZipDownload = async (files, title) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../../../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      alert(`${files.length}개의 파일을 압축 중입니다. 잠시만 기다려 주세요...`);
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const zip = new JSZip();
      const promises = files.map(async (f) => {
        const url = domain + (f.path ? f.path.replace(/\\/g, '/') : '');
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(f.originalName || f.name, blob);
      });
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: 'blob' });
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      saveAs(content, `${title ? title.replace(/[/\\:*?"<>|]/g, '_') : '첨부파일'}_${dateStr}.zip`);
    } catch(e) {
      console.error(e);
      alert('압축 파일 생성 중 오류가 발생했습니다.');
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('WRITE'); // 'WRITE' or 'DETAIL'
  const [hasCustomizedLine, setHasCustomizedLine] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // [수정] 결재선을 직접 건드린 적이 없으면(신규), 기안자는 무조건 현재 로그인 접속자로 강제 고정!
  const currentDrafter = hasCustomizedLine ? (projectInfo?.drafter || userDisplayName) : userDisplayName;
  const currentReviewer = projectInfo?.reviewer || '미지정';
  const currentReviewerPosition = projectInfo?.reviewer_position || '';
  const currentApprover = projectInfo?.approver || '미지정';
  const currentApproverPosition = projectInfo?.approver_position || '';

  // [추가] 결재선 모달에서 실제로 확인 버튼을 눌러 결재선이 확정되었을 때만 커스텀 플래그 활성화
  useEffect(() => {
    if (projectInfo && ((projectInfo.reviewer && projectInfo.reviewer !== '미지정') || (projectInfo.approver && projectInfo.approver !== '미지정'))) {
      setHasCustomizedLine(true);
    }
  }, [projectInfo]);

  const fetchApprovalDocs = async (showFeedback = false) => {
    if (showFeedback) setIsRefreshing(true);
    try {
      const response = await fetch(`/api/safety/aprv/list?assessment_id=${projectInfo?.id || ''}`, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data || []);
      } else {
        console.warn("결재 목록을 불러오지 못했습니다. (서버 API 미구현 혹은 에러)");
      }
    } catch (err) {
      console.error("서버 통신 오류:", err);
    } finally {
      if (showFeedback) {
        setTimeout(() => setIsRefreshing(false), 500); // 0.5초 대기(피드백 인지 목적)
      }
    }
  };

  useEffect(() => {
    fetchApprovalDocs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 문서 필터링
  const getFilteredDocs = () => {
    return documents.filter(doc => {
      // 서버에서 권한 검증이 끝난 데이터이므로 상태값에 맞게 각 탭에 뿌려주기만 함
      if (activeMenu === 'ALL') return true;
      if (activeMenu === 'DRAFT' && !(doc.doc_status === 'draft' || doc.doc_status === 'withdraw')) return false;
      if (activeMenu === 'PENDING' && !isMyTurn(doc)) return false;
      if (activeMenu === 'PROGRESS' && doc.doc_status !== 'progress') return false;
      if (activeMenu === 'COMPLETED' && doc.doc_status !== 'complete') return false;
      if (activeMenu === 'REJECTED' && doc.doc_status !== 'reject') return false;

      // 2. 검색어 필터
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const docTitle = doc.doc_title ? doc.doc_title.toLowerCase() : '';
        const drafterName = (doc.lines && doc.lines.length > 0) ? doc.lines[0].user_name.toLowerCase() : '';

        if (searchCategory === 'ALL') {
          return docTitle.includes(query) || drafterName.includes(query);
        } else if (searchCategory === 'TITLE') {
          return docTitle.includes(query);
        } else if (searchCategory === 'DRAFTER') {
          return drafterName.includes(query);
        }
      }
      return true;
    });
  };

  const itemsPerPage = 10;
  const filteredDocs = getFilteredDocs();
  const totalItems = filteredDocs.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedDocs = filteredDocs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // 신규 기안문 폼 열기
  const handleOpenWrite = () => {
    setSelectedDoc(null);
    setModalMode('WRITE');
    setHasCustomizedLine(false);
    setIsModalOpen(true);
  };

  // 임시저장
  const handleSaveDraft = async (docData) => {
    try {
      const payload = {
        aprv_id: docData.aprv_id || (docData.id !== 'new' ? docData.id : undefined),
        assessment_id: projectInfo?.id || 1, // 원본 프로젝트 ID 매핑
        title: docData.title || '(제목 없음)',
        docNumber: docData.docNumber || '',
        department: docData.department || '',
        content: docData.content || '',
        form_code: 'SAFETY_EVAL',
        public_type: docData.publicScope === '공개' ? 'all' : (docData.publicScope === '부서' ? 'dept' : 'user'),
        lines: (docData.lines && docData.lines.length > 0) ? docData.lines.map(l => ({
          user_id: l.user_id || l.userId || l.userid || '',
          user_name: l.user_name || l.userName || l.name || '',
          user_position: l.user_position || l.userPosition || l.position || ''
        })) : [
          { user_id: user?.userid || user?.user_id, user_name: userDisplayName, user_position: userPosition },
          (currentReviewer && currentReviewer !== '미지정') ? { user_id: projectInfo?.reviewer_id || '', user_name: currentReviewer, user_position: projectInfo?.reviewer_position || '' } : null,
          (currentApprover && currentApprover !== '미지정') ? { user_id: projectInfo?.approver_id || '', user_name: currentApprover, user_position: projectInfo?.approver_position || '' } : null
        ].filter(Boolean)
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      
      let deletedFileIds = [];
      if (docData.attachments) {
        docData.attachments.forEach(file => {
          if (file.file) {
            formData.append('files', file.file);
          } else if (file.isDeleted) {
            deletedFileIds.push(file.id || file.file_id);
          }
        });
      }
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));

      const response = await fetch('/api/safety/aprv/temp_save', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('임시저장이 완료되었습니다.');
        setIsModalOpen(false);
        fetchApprovalDocs();
      } else {
        alert('임시저장 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error("임시저장 통신 오류:", err);
      alert('서버와의 통신에 실패했습니다.');
    }
  };

  const handleUpdateDoc = async (docData) => {
    try {
      const payload = {
        doc_title: docData.title,
        doc_content: docData.content
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      
      let deletedFileIds = [];
      if (docData.attachments) {
        docData.attachments.forEach(file => {
          if (file.file) {
            formData.append('files', file.file);
          } else if (file.isDeleted) {
            deletedFileIds.push(file.id || file.file_id);
          }
        });
      }
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));

      const docId = docData.aprv_id || docData.id;
      const response = await fetch(`/api/safety/aprv/${docId}/update`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('문서 내용이 성공적으로 수정되었습니다.');
        setIsModalOpen(false);
        fetchApprovalDocs();
      } else {
        alert('수정 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error("업데이트 통신 오류:", err);
      alert('서버와의 통신에 실패했습니다.');
    }
  };

  // 상신 (기안 요청)
  const handleDraftSubmit = async (docData) => {
    try {
      const payload = {
        aprv_id: docData.aprv_id || (docData.id !== 'new' ? docData.id : undefined),
        assessment_id: projectInfo?.id || 1, // 원본 프로젝트 ID 매핑
        title: docData.title || '(제목 없음)', // [수정] DB 저장을 위한 제목 누락분 추가
        docNumber: docData.docNumber || '',
        department: docData.department || '',
        content: docData.content || '',
        attachments: docData.attachments || [],
        form_code: 'SAFETY_EVAL',
        public_type: docData.publicScope === '공개' ? 'all' : (docData.publicScope === '부서' ? 'dept' : 'user'),
        lines: (docData.lines && docData.lines.length > 0) ? docData.lines.map(l => ({
          user_id: l.user_id || l.userId || l.userid || '',
          user_name: l.user_name || l.userName || l.name || '',
          user_position: l.user_position || l.userPosition || l.position || ''
        })) : [
          { user_id: user?.userid || user?.user_id, user_name: userDisplayName, user_position: userPosition },
          (currentReviewer && currentReviewer !== '미지정') ? { user_id: projectInfo?.reviewer_id || '', user_name: currentReviewer, user_position: projectInfo?.reviewer_position || '' } : null,
          (currentApprover && currentApprover !== '미지정') ? { user_id: projectInfo?.approver_id || '', user_name: currentApprover, user_position: projectInfo?.approver_position || '' } : null
        ].filter(Boolean)
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(payload));
      
      let deletedFileIds = [];
      if (docData.attachments) {
        docData.attachments.forEach(file => {
          if (file.file) {
            formData.append('files', file.file);
          } else if (file.isDeleted) {
            deletedFileIds.push(file.id || file.file_id);
          }
        });
      }
      formData.append('deletedFileIds', JSON.stringify(deletedFileIds));

      const response = await fetch('/api/safety/aprv/draft', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        alert('결재 상신이 완료되었습니다.');
        setIsModalOpen(false);
        fetchApprovalDocs(); // 성공 후 결재 리스트 즉시 새로고침
      } else {
        alert('결재 상신 중 오류가 발생했습니다.');
      }
    } catch (err) {
      console.error("상신 통신 오류:", err);
      alert('서버와의 통신에 실패했습니다.');
    }
  };

  // 상세 보기 진입
  const handleViewDetail = (doc) => {
    // [수정] 모달창 렌더링 호환성을 위해 DB 데이터를 모달 변수명으로 껍데기 씌워서 전달
    const mappedDoc = {
      ...doc,
      title: doc.doc_title || '',
      docNumber: doc.doc_no || '-',
      department: doc.drafter_dept || '-',
      docStatus: doc.doc_status || 'draft',
      content: doc.doc_content || '',
      attachments: (() => {
        try {
          return doc.attachments ? JSON.parse(doc.attachments) : [];
        } catch (e) {
          return [];
        }
      })(),
      drafterName: doc.lines && doc.lines.length > 0 ? doc.lines[0].user_name : '-',
      drafterPosition: doc.lines && doc.lines.length > 0 ? doc.lines[0].user_position : '-',
      draftDate: doc.make_date ? doc.make_date.substring(0, 10) : '-',
      lines: (doc.lines || []).map(l => {
        let mappedStatus = '대기';
        // 기안자(1번)는 상신 전(draft)이면 '작성중', 상신된 상태면 '기안완료'
        if (l.line_no === 1) mappedStatus = (doc.doc_status === 'draft' || doc.doc_status === 'withdraw') ? '작성중' : '기안완료';
        else if (l.line_status === 'complete' || l.line_status === '승인완료' || l.line_status === '승인') mappedStatus = '승인완료';
        else if (l.line_status === 'reject' || l.line_status === '반려') mappedStatus = '반려';
        else if (l.current_yn === 'y' || l.line_status === 'progress' || l.line_status === '검토중') mappedStatus = '검토중';

        return {
          ...l, // 원본 DB 필드 전체 보존 (line_id, current_yn 등)
          role: l.line_no === 1 ? '기안' : (l.line_no === 2 ? '검토' : '승인'),
          name: l.user_name,
          status: mappedStatus,
          date: l.done_date ? l.done_date.substring(0, 10) : (l.line_no === 1 && doc.make_date ? doc.make_date.substring(0, 10) : '')
        };
      }),
      opinions: doc.opinions || []
    };
    setSelectedDoc(mappedDoc);
    setModalMode('DETAIL');
    setIsModalOpen(true);
  };

  // 승인 처리
  const handleApprove = async (comment) => {
    try {
      const currentLine = selectedDoc.lines?.find(l => l.current_yn === 'y' || l.status === '검토중');
      const lineId = currentLine ? currentLine.line_id : null;

      const response = await fetch(`/api/safety/aprv/${selectedDoc.aprv_id || selectedDoc.id}/process`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'approve', opinion: comment || '', lineId })
      });
      if (response.ok) {
        alert('승인이 완료되었습니다.');
        setIsModalOpen(false);
        fetchApprovalDocs(); // 서버 리스트 최신화
      } else {
        alert('승인 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error(err);
      alert('승인 처리 중 오류가 발생했습니다.');
    }
  };

  // 결재 회수 처리
  const handleWithdrawDoc = async (aprvId) => {
    try {
      const response = await fetch(`/api/safety/aprv/${aprvId}/withdraw`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        alert('결재 회수가 완료되었습니다. (작성중 문서 탭으로 이동)');
        setIsModalOpen(false);
        fetchApprovalDocs(); // 목록 최신화
      } else {
        alert('회수 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error("회수 통신 오류:", err);
      alert('서버와의 통신에 실패했습니다.');
    }
  };

  // 반려 처리
  const handleReject = async (comment) => {
    try {
      const currentLine = selectedDoc.lines?.find(l => l.current_yn === 'y' || l.status === '검토중');
      const lineId = currentLine ? currentLine.line_id : null;

      const response = await fetch(`/api/safety/aprv/${selectedDoc.aprv_id || selectedDoc.id}/process`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'reject', opinion: comment || '', lineId })
      });
      if (response.ok) {
        alert('반려 처리가 완료되었습니다.');
        setIsModalOpen(false);
        fetchApprovalDocs(); // 서버 리스트 최신화
      } else {
        alert('반려 처리에 실패했습니다.');
      }
    } catch (err) {
      console.error(err);
      alert('반려 처리 중 오류가 발생했습니다.');
    }
  };

  // 재기안 (반려 상태에서 다시 기안창으로 가져오기)
  const handleRedraft = (doc) => {
    setSelectedDoc(doc);
    setModalMode('WRITE');
  };

  // 삭제 처리
  const handleDeleteDoc = async (aprvId) => {
    try {
      const response = await fetch(`/api/safety/aprv/${aprvId}`, { 
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('문서가 삭제되었습니다.');
        setIsModalOpen(false);
        fetchApprovalDocs(); // 새로고침
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  return (
    <div>
      <div className="sf-card" style={{ padding: 0 }}>
        {/* 상단 가로 서브탭 메뉴 */}
        <div style={{ display: 'flex', borderBottom: '1px solid #cbd5e1', background: '#f8fafc', padding: '0', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', overflow: 'hidden' }}>
          {[
            { key: 'PENDING', label: '결재할 문서', icon: 'bi bi-inbox' },
            { key: 'DRAFT', label: '임시저장 문서', icon: 'bi bi-pencil-square' },
            { key: 'PROGRESS', label: '진행중 문서', icon: 'bi bi-hourglass-split' },
            { key: 'REJECTED', label: '반려 문서', icon: 'bi bi-x-octagon' },
            { key: 'COMPLETED', label: '완료 문서', icon: 'bi bi-check-circle' },
            { key: 'ALL', label: '전체 문서', icon: 'bi bi-files' }
          ].map((menu, index) => {
            const isSelected = activeMenu === menu.key;
            return (
              <button
                key={menu.key}
                onClick={() => { setActiveMenu(menu.key); setCurrentPage(1); }}
                style={{
                  padding: '14px 20px',
                  paddingLeft: index === 0 ? '22px' : '20px',
                  border: 'none',
                  borderBottom: isSelected ? '3px solid #2563eb' : '3px solid transparent',
                  background: 'transparent',
                  color: isSelected ? '#2563eb' : '#64748b',
                  fontSize: '0.85rem',
                  fontWeight: isSelected ? 700 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s'
                }}
              >
                <i className={menu.icon}></i>
                {menu.label} ({counts[menu.key] || 0})
              </button>
            );
          })}

          <button
            onClick={() => fetchApprovalDocs(true)}
            disabled={isRefreshing}
            style={{
              marginLeft: 'auto',
              marginRight: '20px',
              alignSelf: 'center',
              background: isRefreshing ? '#94a3b8' : '#475569',
              border: 'none',
              borderRadius: '6px',
              color: '#fff',
              padding: '6px 14px',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => !isRefreshing && (e.currentTarget.style.background = '#334155')}
            onMouseLeave={(e) => !isRefreshing && (e.currentTarget.style.background = '#475569')}
          >
            {isRefreshing ? '업데이트 중... ⏳' : '새로고침 🔄'}
          </button>
        </div>

        {/* 컨트롤 영역 (검색창 및 기안 작성) */}
        <div className="sf-card-header" style={{ padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
          {/* 왼쪽: 검색 영역 */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, maxWidth: '650px' }}>
            <select
              value={searchCategory}
              onChange={e => { setSearchCategory(e.target.value); setCurrentPage(1); }}
              style={{ height: '38px', padding: '0 32px 0 12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', outline: 'none', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px top 50%', backgroundSize: '10px auto', flexShrink: 0 }}
            >
              <option value="ALL">전체</option>
              <option value="TITLE">제목</option>
              <option value="DRAFTER">기안자</option>
            </select>
            <input
              type="text"
              className="form-control"
              placeholder=""
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setSearchQuery(searchText);
                  setCurrentPage(1);
                }
              }}
              style={{ flex: 1, minWidth: '150px', fontSize: '0.85rem', padding: '0 12px', height: '38px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
            />
            <button
              className="sf-btn"
              onClick={() => { setSearchQuery(searchText); setCurrentPage(1); }}
              style={{ background: '#475569', color: '#fff', height: '38px', borderRadius: '4px', padding: '0 16px', fontSize: '0.85rem', border: 'none', fontWeight: 600 }}
            >
              검색
            </button>
            <button
              className="sf-btn sf-btn-outline"
              onClick={() => { setSearchText(''); setSearchQuery(''); setSearchCategory('ALL'); setCurrentPage(1); }}
              style={{ whiteSpace: 'nowrap', height: '38px', borderRadius: '4px', padding: '0 16px', fontSize: '0.85rem', background: '#fff', borderColor: '#cbd5e1', color: '#475569' }}
            >
              초기화
            </button>

            {/* 검색어 뱃지 */}
            {searchQuery && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '16px', fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600, flexShrink: 0 }}>
                <span>'{searchQuery}' 검색결과</span>
                <i
                  className="fas fa-times"
                  style={{ cursor: 'pointer', background: '#dbeafe', borderRadius: '50%', padding: '2px 4px', fontSize: '0.7rem' }}
                  onClick={() => { setSearchText(''); setSearchQuery(''); setSearchCategory('ALL'); setCurrentPage(1); }}
                ></i>
              </div>
            )}
          </div>

          {/* 오른쪽: 작성 버튼 */}
          {(!(projectInfo?.status && !['작성 중', '진행 중', 'in_progress'].includes(projectInfo.status)) || user?.role === 'admin') && (
          <button
            onClick={handleOpenWrite}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              padding: '0 18px',
              height: '38px',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
          >
            <i className="bi bi-file-earmark-plus"></i> 새 결재문서 작성
          </button>
          )}
        </div>

        {/* 테이블 */}
        <div style={{ padding: 0, maxWidth: '100%' }}>
          <div className="table-responsive" style={{ width: '100%', overflowX: 'auto', display: 'block' }}>
            {(() => {
              const colCount = (activeMenu === 'PENDING' || activeMenu === 'PROGRESS') ? 6 : 5;
              return (
                <table className="sf-table" style={{ fontSize: '0.88rem', minWidth: '980px', width: '100%', tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px', minWidth: '50px', textAlign: 'center' }}>No</th>
                      <th style={{ width: '110px', textAlign: 'center' }}>문서 상태</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left' }}>제목</th>
                      <th style={{ width: '150px', textAlign: 'center' }}>기안자</th>
                      {activeMenu === 'PENDING' && <th style={{ width: '150px', textAlign: 'center' }}>기안부서</th>}
                      {activeMenu === 'PROGRESS' && <th style={{ width: '150px', textAlign: 'center' }}>현재결재자</th>}
                      <th style={{ width: '150px', textAlign: 'center' }}>기안일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocs.length === 0 ? (
                      <tr>
                        <td colSpan={colCount} style={{ textAlign: 'center', padding: '48px 24px', borderBottom: '1px solid #e2e8f0' }}>
                          <div className="sf-empty" style={{ padding: 0 }}>
                            <div className="sf-empty-icon" style={{ color: '#cbd5e1' }}>
                              <i className={searchQuery ? 'fas fa-search' : 'fas fa-folder-open'} style={{ fontSize: '1.8rem' }}></i>
                            </div>
                            <div className="sf-empty-text" style={{ fontSize: '0.85rem' }}>
                              {searchQuery ? (
                                <>
                                  <span style={{ color: '#2563eb', fontWeight: 700 }}>[{searchQuery}]</span>
                                  {' '}검색결과가 없습니다.
                                </>
                              ) : '조회된 결재 문서가 없습니다.'}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedDocs.map((doc, idx) => (
                        <tr
                          key={doc.id}
                          onClick={() => handleViewDetail(doc)}
                          style={{ cursor: 'pointer', borderBottom: '1px solid #e2e8f0' }}
                        >
                          <td className="text-center" style={{ color: '#64748b', verticalAlign: 'middle' }}>
                            <span className="sf-row-num">
                              {totalItems - ((currentPage - 1) * itemsPerPage + idx)}
                            </span>
                          </td>
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: doc.doc_status === 'draft' ? '#475569' :
                                doc.doc_status === 'withdraw' ? '#ea580c' :
                                  doc.doc_status === 'progress' ? '#b45309' :
                                    doc.doc_status === 'complete' ? '#15803d' : '#b91c1c',
                              background: doc.doc_status === 'draft' ? '#f1f5f9' :
                                doc.doc_status === 'withdraw' ? '#ffedd5' :
                                  doc.doc_status === 'progress' ? '#fef3c7' :
                                    doc.doc_status === 'complete' ? '#dcfce7' : '#fee2e2'
                            }}>
                              {doc.doc_status === 'draft' ? '임시저장' :
                                doc.doc_status === 'withdraw' ? '회수' :
                                  doc.doc_status === 'progress' ? '진행중' :
                                    doc.doc_status === 'complete' ? '결재완료' : '반려됨'}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600, color: '#1e293b', verticalAlign: 'middle', maxWidth: '300px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {doc.doc_title || '(제목 없음)'}
                              </span>
                              {(() => {
                                try {
                                  const atts = doc.attachments ? JSON.parse(doc.attachments) : [];
                                  if (atts.length > 0) {
                                    return (
                                      <React.Fragment key={doc.id || doc.aprv_id || `idx-${idx}`}>
                                        <div 
                                          style={{ 
                                            flexShrink: 0, 
                                            color: filePopover.id === (doc.id || doc.aprv_id || `idx-${idx}`) ? '#1d4ed8' : '#94a3b8', 
                                            fontSize: '0.8rem', 
                                            cursor: 'pointer', 
                                            padding: '4px 6px', 
                                            background: filePopover.id === (doc.id || doc.aprv_id || `idx-${idx}`) ? '#dbeafe' : 'transparent', 
                                            borderRadius: '4px',
                                            transition: 'all 0.2s ease'
                                          }} 
                                          title={`첨부파일`}
                                          onMouseOver={(e) => {
                                            if (filePopover.id !== (doc.id || doc.aprv_id || `idx-${idx}`)) {
                                              e.currentTarget.style.background = '#eff6ff';
                                              e.currentTarget.style.color = '#2563eb';
                                            }
                                          }}
                                          onMouseOut={(e) => {
                                            if (filePopover.id !== (doc.id || doc.aprv_id || `idx-${idx}`)) {
                                              e.currentTarget.style.background = 'transparent';
                                              e.currentTarget.style.color = '#94a3b8';
                                            }
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (filePopover.id === (doc.id || doc.aprv_id || `idx-${idx}`)) {
                                              setFilePopover({ id: null, x: 0, isUp: false, top: 0, bottom: 0 });
                                            } else {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              const popoverHeight = Math.min(atts.length * 36, 180) + 110;
                                              const spaceBelow = window.innerHeight - rect.bottom;
                                              const showUp = spaceBelow < popoverHeight && rect.top > popoverHeight;

                                              setFilePopover({
                                                id: (doc.id || doc.aprv_id || `idx-${idx}`),
                                                x: rect.left,
                                                isUp: showUp,
                                                top: rect.bottom + 4,
                                                bottom: window.innerHeight - rect.top + 4
                                              });
                                            }
                                          }}
                                        >
                                          <i className="fas fa-paperclip"></i>
                                        </div>
                                        {/* 팝오버 UI (React Portal을 이용해 body에 렌더링) */}
                                        {filePopover.id === (doc.id || doc.aprv_id || `idx-${idx}`) && createPortal(
                                          <div 
                                            style={{
                                              position: 'fixed',
                                              top: filePopover.isUp ? 'auto' : `${filePopover.top}px`,
                                              bottom: filePopover.isUp ? `${filePopover.bottom}px` : 'auto',
                                              left: `${filePopover.x}px`,
                                              background: 'white',
                                              border: '1px solid #cbd5e1',
                                              borderRadius: '6px',
                                              boxShadow: '0 4px 10px -1px rgba(0, 0, 0, 0.15)',
                                              zIndex: 9999,
                                              width: '380px',
                                              padding: '10px',
                                              cursor: 'default'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e2e8f0', margin: '-10px -10px 10px -10px', padding: '8px 12px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderBottom: '1px solid #cbd5e1' }}>
                                              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>첨부파일 목록 ({atts.length})</span>
                                              <i className="fas fa-times" style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' }} onClick={() => setFilePopover({ id: null, x: 0, isUp: false, top: 0, bottom: 0 })}></i>
                                            </div>
                                            <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                              {atts.map((mf, mfIdx) => (
                                                <div key={mf.id || mfIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: '4px', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                                  <a href="#!" onClick={(e) => { e.preventDefault(); handleView(mf); }} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px', color: '#1e293b', textDecoration: 'none', display: 'flex', alignItems: 'center' }} title={`${mf.originalName || mf.name} 열기`}>
                                                    <i className="far fa-file-alt" style={{ marginRight: '6px', color: '#64748b' }}></i>
                                                    <span style={{ cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.textDecoration='underline'} onMouseOut={e => e.currentTarget.style.textDecoration='none'}>{mf.originalName || mf.name}</span>
                                                  </a>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{formatBytes(mf.size)}</span>
                                                    <i className="fas fa-download" style={{ color: '#2563eb', cursor: 'pointer' }} title="다운로드" onClick={() => handleDownload(mf)}></i>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                                              <button 
                                                onClick={() => handleZipDownload(atts, doc.doc_title || '전자결재')}
                                                style={{ 
                                                width: '100%', padding: '8px 0', background: '#eff6ff', border: '1px solid #bfdbfe', 
                                                borderRadius: '4px', color: '#1d4ed8', fontSize: '0.75rem', fontWeight: 700, 
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' 
                                              }}>
                                                <i className="fas fa-file-archive" style={{ color: '#ea580c', fontSize: '0.85rem' }}></i>
                                                {atts.length === 1 ? '압축(ZIP) 다운로드' : '전체 압축(ZIP) 다운로드'}
                                              </button>
                                            </div>
                                          </div>,
                                          document.body
                                        )}
                                      </React.Fragment>
                                    );
                                  }
                                } catch(e) {}
                                return null;
                              })()}
                            </div>
                          </td>
                          <td className="text-center" style={{ color: '#334155', verticalAlign: 'middle' }}>
                            {doc.lines && doc.lines.length > 0 ? doc.lines[0].user_name : '미상'}
                          </td>
                          {activeMenu === 'PENDING' && (
                            <td className="text-center" style={{ color: '#334155', verticalAlign: 'middle' }}>
                              {doc.drafter_dept || doc.department || doc.dept_name || '-'}
                            </td>
                          )}
                          {activeMenu === 'PROGRESS' && (
                            <td className="text-center" style={{ color: '#334155', verticalAlign: 'middle' }}>
                              {doc.lines && doc.lines.length > 0
                                ? (doc.lines.find(l => l.current_yn === 'y' || l.status === '검토중')?.user_name || '-')
                                : '-'}
                            </td>
                          )}
                          <td className="text-center" style={{ color: '#64748b', verticalAlign: 'middle' }}>
                            {doc.make_date ? doc.make_date.substring(0, 10) : ''}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>

        {/* 페이지네이션 푸터 */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          <div style={{
            position: 'absolute', left: '24px',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
            padding: '4px 12px', borderRadius: '20px',
            fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#2563eb' }}>{totalItems === 0 ? 0 : currentPage}</span>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#93c5fd', margin: '0 -2px' }}>/</span>
            <span>{totalItems === 0 ? 0 : Math.max(1, totalPages)}</span>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#bfdbfe', margin: '0 2px' }}>|</span>
            <span>총 {totalItems}건</span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(() => {
              const PAGE_BLOCK_SIZE = 10;
              const safeTotalPages = Math.max(1, totalPages);
              const startPage = Math.floor((currentPage - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
              const endPage = Math.min(startPage + PAGE_BLOCK_SIZE - 1, safeTotalPages);
              const pagesInBlock = Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);

              return (
                <>
                  {/* 처음으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1 || totalItems === 0}
                    style={{
                      opacity: (currentPage === 1 || totalItems === 0) ? 0.4 : 1,
                      cursor: (currentPage === 1 || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === 1 || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="처음으로"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  {/* 이전 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.max(startPage - 1, 1))}
                    disabled={startPage === 1 || totalItems === 0}
                    style={{
                      opacity: (startPage === 1 || totalItems === 0) ? 0.4 : 1,
                      cursor: (startPage === 1 || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (startPage === 1 || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="이전 10페이지"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {pagesInBlock.map(page => (
                    <button
                      key={page}
                      className={`sf-btn sf-btn-sm ${currentPage === page && totalItems > 0 ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                      onClick={() => setCurrentPage(page)}
                      disabled={totalItems === 0}
                      style={{
                        minWidth: 36, borderRadius: 8,
                        opacity: totalItems === 0 ? 0.5 : 1,
                        cursor: totalItems === 0 ? 'default' : 'pointer',
                        pointerEvents: totalItems === 0 ? 'none' : 'auto'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  {/* 다음 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.min(endPage + 1, safeTotalPages))}
                    disabled={endPage === safeTotalPages || totalItems === 0}
                    style={{
                      opacity: (endPage === safeTotalPages || totalItems === 0) ? 0.4 : 1,
                      cursor: (endPage === safeTotalPages || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (endPage === safeTotalPages || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="다음 10페이지"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                  {/* 마지막으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(safeTotalPages)}
                    disabled={currentPage === safeTotalPages || totalItems === 0}
                    style={{
                      opacity: (currentPage === safeTotalPages || totalItems === 0) ? 0.4 : 1,
                      cursor: (currentPage === safeTotalPages || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === safeTotalPages || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="마지막으로"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <SafetyApprovalModal
          mode={modalMode}
          initialData={selectedDoc}
          onClose={() => setIsModalOpen(false)}
          onSaveDraft={handleSaveDraft}
          onUpdateDoc={handleUpdateDoc}
          onSubmitDoc={handleDraftSubmit}
          onApprove={handleApprove}
          onReject={handleReject}
          onDeleteDoc={handleDeleteDoc}
          onWithdrawDoc={handleWithdrawDoc}
          onStartEdit={() => handleRedraft(selectedDoc)}
          onOpenApprovalModal={() => {
            let effReviewer = currentReviewer;
            let effApprover = currentApprover;
            
            if (modalMode === 'WRITE' && !selectedDoc && !hasCustomizedLine) {
              effReviewer = '미지정';
              effApprover = '미지정';
            } else if (selectedDoc && selectedDoc.lines && selectedDoc.lines.length > 0) {
              const lines = selectedDoc.lines;
              if (lines.length === 2) {
                effReviewer = '미지정';
                effApprover = lines[1].user_name || lines[1].userName || '미지정';
              } else if (lines.length >= 3) {
                effReviewer = lines[1].user_name || lines[1].userName || '미지정';
                effApprover = lines[lines.length - 1].user_name || lines[lines.length - 1].userName || '미지정';
              }
            }

            const currentLines = {
              drafter: { name: currentDrafter },
              reviewer: effReviewer && effReviewer !== '미지정' ? { name: effReviewer } : null,
              approver: effApprover && effApprover !== '미지정' ? { name: effApprover } : null
            };
            if (onOpenApprovalModal) onOpenApprovalModal(currentLines);
          }}
          currentDrafter={currentDrafter}
          currentReviewer={(() => {
            if (modalMode === 'WRITE' && !selectedDoc && !hasCustomizedLine) return '미지정';
            if (selectedDoc && selectedDoc.lines && selectedDoc.lines.length > 0) {
              if (selectedDoc.lines.length === 2) return '미지정';
              if (selectedDoc.lines.length >= 3) return selectedDoc.lines[1].user_name || selectedDoc.lines[1].userName || '미지정';
            }
            return currentReviewer;
          })()}
          currentReviewerPosition={(() => {
            if (modalMode === 'WRITE' && !selectedDoc && !hasCustomizedLine) return '';
            if (selectedDoc && selectedDoc.lines && selectedDoc.lines.length >= 3) {
              return selectedDoc.lines[1].user_position || selectedDoc.lines[1].userPosition || '';
            }
            return currentReviewerPosition;
          })()}
          currentApprover={(() => {
            if (modalMode === 'WRITE' && !selectedDoc && !hasCustomizedLine) return '미지정';
            if (selectedDoc && selectedDoc.lines && selectedDoc.lines.length > 0) {
              if (selectedDoc.lines.length === 2) return selectedDoc.lines[1].user_name || selectedDoc.lines[1].userName || '미지정';
              if (selectedDoc.lines.length >= 3) return selectedDoc.lines[selectedDoc.lines.length - 1].user_name || selectedDoc.lines[selectedDoc.lines.length - 1].userName || '미지정';
            }
            return currentApprover;
          })()}
          currentApproverPosition={(() => {
            if (modalMode === 'WRITE' && !selectedDoc && !hasCustomizedLine) return '';
            if (selectedDoc && selectedDoc.lines && selectedDoc.lines.length > 0) {
              if (selectedDoc.lines.length === 2) return selectedDoc.lines[1].user_position || selectedDoc.lines[1].userPosition || '';
              if (selectedDoc.lines.length >= 3) return selectedDoc.lines[selectedDoc.lines.length - 1].user_position || selectedDoc.lines[selectedDoc.lines.length - 1].userPosition || '';
            }
            return currentApproverPosition;
          })()}
          hasCustomizedLine={hasCustomizedLine}
        />
      )}
    </div>
  );
}

export default SafetyApprovalTab;
